import { css } from 'styled-components';

import { ResponseTone } from '../types';

/*
  The full page's response scale, read off the Figma 6566:9272 chat frames, and the side panel's
  beside it (end of file). Every response type reads its numbers through `rsv` / `responseType`,
  which resolve the variant from the nearest `ResponseScaleProvider`. Colours are literals, never
  palette tokens or `tint()`: the uat palette is `var()` references, which neither survive an antd
  portal nor take an alpha suffix.

  Line heights the dump leaves unresolved are the nearest clean value to the drawn box; they are
  marked `~`.
*/

export const rsColor = {
  page: '#05090F',
  panel: '#101830',
  inset: '#0D1428',
  border: '#212D4B',
  text: '#F5F7FB',
  secondary: '#AEBBD0',
  muted: '#64709A',
  link: '#268AFF',
  primary: '#4F46E5',
  disabled: '#98A5BF',
  white: '#FFFFFF',
  success: '#1EB980',
  warning: '#F5A623',
  danger: '#EE6475',
  violet: '#6D5DFC',
  // 6422:10677 / 6265:4213: the question bubbles' glow.
  bubbleGlow: '0 10px 26px rgba(38, 138, 255, 0.18)',
  // 6424:11849: the governed panel's 1px spread at 12%.
  governedGlow: '0 0 0 1px rgba(38, 138, 255, 0.12)',
  // 6422:10677: the dump has no stops; these are the user bubble's (6265:4213).
  questionBubble: 'linear-gradient(135deg, #268AFF 0%, #1B6FD1 100%)'
} as const;

/** Growth Planner hand-off frames 6473:12314-13248 draw on their own hues. */
export const rsHandoffColor = {
  title: '#EEF2FF',
  cardRule: '#E4E8F0',
  selectedFill: 'rgba(101, 88, 245, 0.07)',
  selectedRule: '#3D8BFF',
  checkbox: '#1C77F6',
  unavailable: '#DC5165',
  passed: '#159F73',
  caution: '#D97706',
  spinner: '#8C83FF',
  track: '#1B2438',
  view: '#7469FF',
  sent: '#35C99A',
  sentFill: 'rgba(53, 201, 154, 0.14)',
  failed: '#FF778A',
  failedFill: 'rgba(255, 119, 138, 0.14)',
  retryInk: '#1A0D10'
} as const;

export interface IResponseToneColor {
  ink: string;
  /** The ink at the design's 14% paint opacity. */
  fill: string;
}

export const RESPONSE_TONE: Record<ResponseTone, IResponseToneColor> = {
  success: { ink: '#1EB980', fill: 'rgba(30, 185, 128, 0.14)' },
  warning: { ink: '#F5A623', fill: 'rgba(245, 166, 35, 0.14)' },
  error: { ink: '#EE6475', fill: 'rgba(238, 100, 117, 0.14)' },
  info: { ink: '#268AFF', fill: 'rgba(38, 138, 255, 0.14)' },
  neutral: { ink: '#64709A', fill: 'rgba(100, 112, 154, 0.14)' },
  accent: { ink: '#6D5DFC', fill: 'rgba(109, 93, 252, 0.14)' }
};

/** A tone the client does not know reads as neutral rather than failing the render. */
export const responseTone = (tone: ResponseTone | null | undefined): IResponseToneColor =>
  (tone !== null && tone !== undefined && RESPONSE_TONE[tone]) || RESPONSE_TONE.neutral;

/**
 * 6424:11869 / 11875 / 11881 / 11887. The third is drawn `#101830`, the panel's own fill, so the
 * At-Risk segment is invisible; it stays the danger hue until the designer confirms.
 */
export const RESPONSE_SERIES = ['#6D5DFC', '#268AFF', '#EE6475', '#64709A', '#F5A623', '#1EB980'];

export interface IResponseTypeStep {
  size: number;
  lineHeight: number;
  weight: 400 | 600 | 700 | 800;
  letterSpacing?: number;
  /** Set only where a variant changes the case; otherwise the element's own rule stands. */
  textTransform?: 'uppercase' | 'none';
}

export const rsType = {
  // 6265:4221: the reply's intro line.
  intro: { size: 14.5, lineHeight: 23, weight: 400 },
  // 6422:10677: Orbit's question bubble.
  question: { size: 14.5, lineHeight: 22, weight: 400 },
  // 6424:11856: the governed panel's prose. ~
  prose: { size: 15, lineHeight: 23, weight: 400 },
  // 6265:4223: every panel title.
  panelTitle: { size: 15, lineHeight: 18, weight: 700 },
  // 6424:11853: the GOVERNED ANALYSIS eyebrow.
  eyebrow: { size: 12, lineHeight: 15, weight: 700, letterSpacing: 1.2 },
  // 6265:4681: "ANSWER 1 OF 3".
  answerLabel: { size: 11.5, lineHeight: 14, weight: 700, letterSpacing: 1.265 },
  // 6265:4765: "Week of 08 Sep 2026".
  subtitle: { size: 12.5, lineHeight: 15, weight: 400 },
  // 6265:4256: key-value labels and values, field values.
  body: { size: 14, lineHeight: 21, weight: 400 },
  // 6265:4595: a stacked field's label.
  fieldLabel: { size: 13, lineHeight: 16, weight: 400 },
  // 6604:1488: table cells, answer and section bodies, banner body. ~
  cell: { size: 13.5, lineHeight: 17, weight: 400 },
  // 6604:1496: a status cell, Semi Bold in its tone.
  cellStatus: { size: 13.5, lineHeight: 17, weight: 600 },
  // 6265:4231: table header cells, upper-cased.
  tableHead: {
    size: 11.5,
    lineHeight: 14,
    weight: 700,
    letterSpacing: 0.575,
    textTransform: 'uppercase'
  },
  // 6604:1493: the severity pill.
  pill: { size: 11, lineHeight: 13, weight: 800 },
  // 6265:4683: a multi-answer row title.
  rowTitle: { size: 16, lineHeight: 20, weight: 700 },
  // 6265:4784: an accordion head.
  sectionTitle: { size: 14.5, lineHeight: 18, weight: 700 },
  // 6265:4810: an accordion head's count.
  count: { size: 13, lineHeight: 16, weight: 700 },
  // 6424:11867: legend labels and values.
  legend: { size: 14.5, lineHeight: 18, weight: 400 },
  // 6424:11865: the donut's centre value.
  total: { size: 28, lineHeight: 34, weight: 700 },
  // 6424:11862: the donut's centre label.
  totalLabel: { size: 10.5, lineHeight: 13, weight: 700, letterSpacing: 1.47 },
  // 6424:11898: the confidence pill.
  confidence: { size: 14.5, lineHeight: 18, weight: 700 },
  // 6424:11900: the reason line.
  reason: { size: 13, lineHeight: 20, weight: 400 },
  // 6265:4607: every button label.
  button: { size: 14, lineHeight: 17, weight: 700 },
  // 6604:1791: link actions.
  link: { size: 13.5, lineHeight: 16, weight: 600 },
  // 6265:4686: a multi-answer row's links.
  answerLink: { size: 13.5, lineHeight: 16, weight: 600 },
  // 6459:17503: a form result card's title.
  resultTitle: { size: 14, lineHeight: 18, weight: 700 },
  // 6459:17505: "Ticket ID:".
  resultLabel: { size: 11.5, lineHeight: 14, weight: 400 },
  // 6459:17508: "Submitted: …".
  caption: { size: 11, lineHeight: 14, weight: 400 },
  // 6265:4614: the renewal success banner title.
  bannerTitle: { size: 15, lineHeight: 18, weight: 700 },
  // 6473:12292: the compact "Orbit is analyzing..." line.
  statusLine: { size: 12, lineHeight: 16, weight: 600 },
  // 6473:11344: a hand-off step heading.
  handoffHeading: { size: 11, lineHeight: 14, weight: 700 },
  // 6473:11349: a metric card title.
  metricTitle: { size: 13, lineHeight: 16, weight: 700 },
  // 6473:11350: a metric card meta line, the unavailable line.
  metricMeta: { size: 11, lineHeight: 14, weight: 400 },
  // 6473:11380: checks table and summary.
  handoffBody: { size: 12, lineHeight: 15, weight: 400 },
  // 6473:11399: Continue to Growth Planner.
  continue: { size: 16, lineHeight: 19, weight: 700 },
  // 6473:11920 / 13239: a hand-off result title.
  handoffTitle: { size: 17, lineHeight: 21, weight: 700 },
  // 6473:13242: a hand-off result body. ~
  handoffMessage: { size: 13, lineHeight: 18, weight: 400 },
  // 6473:11948: View in Growth Planner / Retry.
  handoffButton: { size: 13, lineHeight: 16, weight: 700 },
  // 6265:4685 / 6424:11915: an answer row's body, the report banner's body.
  answerBody: { size: 13.5, lineHeight: 22, weight: 400 },
  // 6265:4618: the renewal banner's two lines.
  bannerBody: { size: 13.5, lineHeight: 22, weight: 400 },
  // 6422:10649: a boxed read-only value.
  boxed: { size: 14, lineHeight: 22, weight: 400 },
  // 6424:11919: a check bullet.
  check: { size: 13.5, lineHeight: 16, weight: 400 },
  // 6265:4786 / 4794: an accordion's body and list.
  sectionBody: { size: 13.5, lineHeight: 23, weight: 400 },
  // 6265:4604: entered textarea content.
  textarea: { size: 14, lineHeight: 23, weight: 400 },
  // 6265:4596: an editable field's value.
  fieldValue: { size: 14, lineHeight: 21, weight: 400 },
  // 6473:11917: a hand-off result badge's glyph.
  badgeGlyph: { size: 26, lineHeight: 31, weight: 400 }
} satisfies Record<string, IResponseTypeStep>;

export type ResponseTypeStep = keyof typeof rsType;

export const rsMono = "'IBM Plex Mono', SFMono-Regular, Menlo, Consolas, monospace";

/** One type step at the nearest provider's variant; `weight` overrides the step's own. */
export const responseType =
  (step: ResponseTypeStep, weight?: IResponseTypeStep['weight']) => (props: IScaleProps) => {
    const { size, lineHeight, weight: own, letterSpacing, textTransform } = rsFor(props).type[step];
    return css`
      font-size: ${size}px;
      line-height: ${lineHeight}px;
      font-weight: ${weight ?? own};
      letter-spacing: ${letterSpacing ?? 0}px;
      ${textTransform ? `text-transform: ${textTransform};` : ''}
    `;
  };

export const rsSize = {
  // 6422:10613 / 6424:11849: the reply column; multi-answer alone draws 845 (6265:4674).
  replyColumn: 888,
  replyColumnNarrow: 845,
  // 6473:12314: the hand-off chooser and its result cards.
  handoffColumn: 960,
  panelHead: 50,
  row: 47,
  tableHeadRow: 42.5,
  tableRow: 50,
  sectionHead: 46,
  button: 44,
  iconButton: 34,
  field: 44,
  textarea: 78.6,
  headIcon: 17,
  buttonIcon: 16,
  // 6265:4596: a field's leading glyph.
  fieldIcon: 16,
  linkIcon: 15,
  iconButtonGlyph: 18,
  // 6459:16509 / 17500: a ticket state card's circle.
  resultIcon: 56,
  // 6265:4612: the renewal banner's tick.
  bannerTick: 32,
  // 6473:11916: a hand-off result badge.
  handoffBadge: 60,
  handoffSpinner: 52,
  progressTrackWidth: 260,
  progressTrackHeight: 4,
  // 6473:13232: a result card's floor; 6473:13236: the spinner's stroke.
  handoffCardMinHeight: 238,
  handoffSpinnerStroke: 4,
  // 6473:11968: Retry, the narrower result button.
  handoffButtonMinWidth: 154,
  // 6473:12858: the summary note's glyph.
  noteIcon: 14,
  metricCardWidth: 472,
  metricCardMinHeight: 62,
  checkbox: 17,
  // 6265:4227: the ticket table's design widths, of 803.
  ticketColumns: [120.4, 269, 120.1, 151.2, 142.3],
  // 6422:10626: a form row's label column.
  fieldLabelColumn: 230,
  // 6459:17501: the result circle's glyph.
  resultGlyph: 24,
  // 6459:17510: the Submitted clock.
  captionIcon: 10,
  // 6265:4782: an accordion chevron.
  chevron: 16,
  // 6424:11910 / 11918: the banner tick's check, a bullet's check.
  bannerTickGlyph: 17,
  checkIcon: 15,
  // 6424:11852 / 11897: the eyebrow and confidence glyphs.
  eyebrowIcon: 16,
  confidenceIcon: 17,
  // 6424:11858-11859: a 200 box, the ring's centre line at r 70, stroke 26.
  donut: 200,
  donutRadius: 70,
  donutRing: 26,
  // 6424:11868 / 11869.
  legendRow: 40,
  legendDot: 11
} as const;

export const rsRadius = {
  panel: 14,
  row: 12,
  button: 10,
  field: 10,
  box: 9,
  iconButton: 9,
  chip: 6,
  pill: 999,
  progress: 4,
  // 6422:10677, TL TR BR BL: Orbit's question bubble.
  questionBubble: '5px 14px 14px 14px'
} as const;

export const rsSpace = {
  introToPanel: 13.5,
  panelHead: '15.5px 20px',
  panelBody: '18px 20px',
  // A panel the payload sends no title for opens on its body.
  headlessBodyTop: 18,
  headIconGap: 10,
  row: '13px 0',
  // The side between an inline row's rule and its text, and between its label and value.
  rowInset: 0,
  rowGap: 16,
  // 6265:4288: a stacked row's label over its body.
  stackedGap: 6,
  // 6604:1493.
  pill: '5px 11px',
  tableCell: '16.5px 10px',
  tableHead: '14px 10px 13.5px',
  tableInset: 20,
  button: '12.5px 20px',
  buttonSecondary: '12.5px 20px',
  buttonGap: 11,
  buttonIconGap: 9,
  buttonRowTop: 18,
  linkIconGap: 8,
  // 6422:10650: a boxed read-only row, its label column and the box.
  boxedRow: '13px 0',
  boxedLabelGap: 16,
  boxedField: '11px 14px',
  field: '12.5px 15px',
  textarea: '12px 15px',
  fieldLabelTop: 16,
  fieldLabelGap: 8,
  questionBubble: 13,
  answerRowGap: 13,
  answerRow: '18px 19px 23px 19px',
  answerPanel: '22px 20px 18px 20px',
  // 6265:4680-4686: label → title → body → link.
  answerLabelToTitle: 9,
  answerTitleToBody: 7,
  answerBodyToLink: 15.5,
  sectionHead: '14px 16px',
  sectionBody: '0 16px 15px 16px',
  resultCard: 22,
  resultGap: 14,
  banner: '18px 20px',
  bannerGap: 15,
  metricColumnGap: 16,
  metricRowGap: 10,
  // 6265:4226-4244: the ticket table's body and its footer link.
  tableBody: '6px 20px 0',
  tableFooter: '6px 20px 21px',
  // 6459:17481-17510: the result card under a form.
  resultTop: 28,
  resultBox: '42px 22px 46px',
  resultMetaGap: 8,
  chip: '5px 12px',
  chipGap: 9,
  captionIconGap: 4,
  // 6265:4611-4618: the renewal banner below its panel.
  bannerTop: 50,
  bannerTextGap: 7,
  // 6265:4762-4820: the weekly update.
  weeklyHead: '16px 20px 17px',
  subtitleGap: 6,
  rowsToSections: 9,
  sectionGap: 10,
  sectionList: '0 16px 21px 57.5px',
  sectionListGap: 6,
  sectionsToActions: 17,
  // 6424:11909-11924: the report banner.
  bannerTitleToBody: 7,
  bannerChecksTop: 11.5,
  bannerCheckGap: 9,
  bannerCheckIconGap: 9,
  bannerActionsTop: 17,
  // 6424:11850-11902: the governed panel.
  panelHeadTight: '15px 20px',
  governedBody: '16.75px 20px 18px 20px',
  governedGap: 16,
  chartCard: '24px 24px 24px 39px',
  chartGap: 47,
  donutTotalGap: 6,
  legendRowGap: 2,
  legendDotGap: 12,
  legendToFoot: 8,
  chartActionGap: 4,
  confidencePill: '12.5px 18px',
  confidenceIconGap: 9,
  reasonGap: 12.8,
  // 6473:12803-12859: the hand-off chooser. The status line's top is level with the reply mark's.
  statusLineTop: 4,
  chooserInset: 7,
  chooserTop: 43,
  metricGridTop: 10,
  metricCard: '14px 14px 14px 50px',
  metricBoxTop: 16,
  metricBoxLeft: 18,
  metricMetaGap: 2,
  metricReasonGap: 5,
  checksTop: 13,
  checksTableTop: 18,
  checksColumnGap: 16,
  checkRow: '10px 12px 9px',
  summaryTop: 44,
  summary: '16px 0 0 24px',
  noteTop: 7,
  noteIconGap: 5,
  continueTop: 10,
  continueGap: 10,
  continueButton: '11px 20px',
  // 6473:12607 / 12872 / 13248: the result cards under Continue.
  handoffSendingTop: 58,
  handoffResultTop: 70,
  handoffRingToTitle: 22,
  handoffBadgeToTitle: 21,
  handoffTitleToMessage: 7,
  progressTop: 29,
  handoffActionsTop: 13,
  // 6473:11948 / 11968: View in Growth Planner, Retry.
  handoffButton: '11px 24px 12px',
  handoffRetryButton: '12px 24px 13px'
} as const;

/** The page draws the 6566:9272 scale; the side panel draws the 6034 / 6098 row's (spec §5). */
export type ResponseVariant = 'page' | 'panel';

type Widen<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K] extends number ? number : readonly number[];
};

/** The colours a variant redraws; every other colour is `rsColor`. */
export interface IResponseRoleColor {
  /** Row, field and column labels, a subtitle, a count. */
  label: string;
  answerRow: string;
  boxed: string;
  fieldRule: string;
}

export interface IResponseScale {
  type: Record<ResponseTypeStep, IResponseTypeStep>;
  size: Widen<typeof rsSize>;
  radius: Widen<typeof rsRadius>;
  space: Widen<typeof rsSpace>;
  color: IResponseRoleColor;
}

const PAGE_SCALE: IResponseScale = {
  type: rsType,
  size: rsSize,
  radius: rsRadius,
  space: rsSpace,
  color: {
    label: rsColor.muted,
    answerRow: rsColor.inset,
    boxed: rsColor.inset,
    fieldRule: rsColor.border
  }
};

/*
  The side panel. What its frames do not draw keeps the page's proportions, anchored on the chrome
  they do draw: type on the title (12.5 of 15), sizes on the head (38 of 50), spacing on the body
  inset (13 of 20), radii on the panel (12 of 14), each rounded to the half pixel.
*/
const PANEL_RATIO = { type: 12.5 / 15, size: 38 / 50, space: 13 / 20, radius: 12 / 14 } as const;

const PANEL_COLUMN = 445;

const toHalf = (value: number): number => Math.round(value * 2) / 2;

const deriveValue = (value: number | string | readonly number[], ratio: number) => {
  if (typeof value === 'number') {
    return toHalf(value * ratio);
  }
  if (typeof value === 'string') {
    return value.replace(/\d+(?:\.\d+)?(?=px)/g, px => String(toHalf(Number(px) * ratio)));
  }
  return value.map(item => toHalf(item * ratio));
};

const derive = <T extends Record<string, number | string | readonly number[]>>(
  page: T,
  ratio: number
): Widen<T> =>
  Object.fromEntries(
    Object.entries(page).map(([key, value]) => [key, deriveValue(value, ratio)])
  ) as Widen<T>;

const deriveType = (step: IResponseTypeStep): IResponseTypeStep => ({
  ...step,
  size: toHalf(step.size * PANEL_RATIO.type),
  lineHeight: toHalf(step.lineHeight * PANEL_RATIO.type),
  letterSpacing: step.letterSpacing && toHalf(step.letterSpacing * PANEL_RATIO.type)
});

// Spec §5 and the frames it cites; 6098:* unless marked.
const PANEL_TYPE: Partial<IResponseScale['type']> = {
  // 6098:2252: the bare intro line.
  intro: { size: 12.5, lineHeight: 19, weight: 400 },
  // 6098:2256 / 3098: the title, then the weekly subtitle in a 15 box.
  panelTitle: { size: 12.5, lineHeight: 15, weight: 700 },
  subtitle: { size: 10.5, lineHeight: 15, weight: 400 },
  // 6098:2323: key-value labels and values, a 14 line in a 16 box.
  body: { size: 11.5, lineHeight: 16, weight: 400 },
  // 6098:2588 / 2592 / 2598: a stacked field's label, value and textarea.
  fieldLabel: { size: 11, lineHeight: 15, weight: 400 },
  fieldValue: { size: 12, lineHeight: 15, weight: 400 },
  textarea: { size: 12, lineHeight: 18, weight: 400 },
  // 6034:4851: a boxed read-only value.
  boxed: { size: 11.5, lineHeight: 17.5, weight: 400 },
  // 6098:2274 / 2281 / 2262 / 2279: cells, a Bold status, the head (not upper-cased), the pill.
  cell: { size: 10.5, lineHeight: 13, weight: 400 },
  cellStatus: { size: 10.5, lineHeight: 13, weight: 700 },
  tableHead: { size: 9.5, lineHeight: 11, weight: 700, textTransform: 'none' },
  pill: { size: 9, lineHeight: 11, weight: 700 },
  // 6692:1500: the table's footer link.
  link: { size: 11, lineHeight: 13, weight: 700 },
  // 6034:5077 / 5079 / 5081 / 6692:1224: a multi-answer row.
  answerLabel: { size: 10, lineHeight: 15, weight: 700, letterSpacing: 0.4 },
  rowTitle: { size: 12.5, lineHeight: 19, weight: 700 },
  answerBody: { size: 11.5, lineHeight: 17.5, weight: 400 },
  answerLink: { size: 11.5, lineHeight: 18, weight: 700 },
  // 6098:3114 / 3140 / 3116: an accordion head, its count, its body.
  sectionTitle: { size: 12, lineHeight: 15, weight: 700 },
  count: { size: 11, lineHeight: 13, weight: 700 },
  sectionBody: { size: 11.5, lineHeight: 18, weight: 400 },
  // 6098:2601.
  button: { size: 12, lineHeight: 15, weight: 700 },
  // 6098:2610 / 2612: the renewal banner.
  bannerTitle: { size: 12.5, lineHeight: 15, weight: 700 },
  bannerBody: { size: 11.5, lineHeight: 17.5, weight: 400 }
};

const PANEL_SPACE: Partial<IResponseScale['space']> = {
  // 6098:2250 / 2254 / 2570: intro 9 over the panel; head 11 / 13; body inset 13.
  introToPanel: 9,
  panelHead: '11px 13px',
  panelBody: '4px 13px 11px',
  // 6098:2320: the ticket details panel has no head.
  headlessBodyTop: 8,
  headIconGap: 8,
  // 6098:2571: 35 rows, centred, the text 40 in from the rule's ends, 10 between label and value.
  row: '9px 0',
  rowInset: 40,
  rowGap: 10,
  // 6098:2352 / 2278.
  stackedGap: 4,
  pill: '4px 8px',
  // 6098:2258 / 2261 / 2273 / 2308: the table inset 13, its head and cells, the footer link.
  tableBody: '4px 13px 0',
  tableHead: '9px 6px 8.5px',
  tableCell: '11.5px 6px 10.5px',
  tableFooter: '3px 13px 13px',
  // 6098:2599-2602: primary 35 and secondary 37 under one 1px rule, 8 apart, 13 under the body.
  button: '9px 14px',
  buttonSecondary: '10px 14px',
  buttonGap: 8,
  buttonIconGap: 7,
  buttonRowTop: 13,
  linkIconGap: 6,
  // 6034:4846 / 4849: a boxed row, its 8 gap and the box.
  boxedRow: '7px 0',
  boxedLabelGap: 8,
  boxedField: '7px 8px',
  // 6098:2586-2596: a stacked field.
  field: '9px 11px',
  textarea: '10px 12px',
  fieldLabelTop: 11,
  fieldLabelGap: 6,
  // 6034:5072-5082: the multi-answer list.
  answerPanel: '11px 13px',
  answerRowGap: 8,
  answerRow: '10px 11px 15.5px 11px',
  answerLabelToTitle: 4,
  answerTitleToBody: 4,
  answerBodyToLink: 7.5,
  // 6098:3092-3150: the weekly head, its subtitle 2 under the title's padded box, the accordions.
  weeklyHead: '11px 13px',
  subtitleGap: 5,
  rowsToSections: 6,
  sectionGap: 10,
  sectionHead: '10px 12px',
  sectionBody: '0 12px 11px',
  sectionList: '0 12px 15px 69px',
  sectionListGap: 4,
  sectionsToActions: 14,
  // 6098:2605: the renewal banner.
  banner: '13px',
  bannerGap: 11,
  bannerTop: 16.5,
  bannerTextGap: 4
};

const panelSpace: IResponseScale['space'] = {
  ...derive(rsSpace, PANEL_RATIO.space),
  ...PANEL_SPACE
};

// The hand-off keeps its chooser inset inside the column.
const panelHandoffColumn = PANEL_COLUMN - panelSpace.chooserInset;

const PANEL_SIZE: Partial<IResponseScale['size']> = {
  // 6098:2253: every type caps at the reply column.
  replyColumn: PANEL_COLUMN,
  replyColumnNarrow: PANEL_COLUMN,
  handoffColumn: panelHandoffColumn,
  metricCardWidth: (panelHandoffColumn - panelSpace.metricColumnGap) / 2,
  // 6098:2254 / 2321 / 2260 / 2272 / 3111.
  panelHead: 38,
  row: 35,
  tableHeadRow: 29.5,
  tableRow: 36,
  sectionHead: 35,
  // 6098:2602 / 2589 / 2590 / 2596.
  button: 35,
  field: 35,
  fieldIcon: 14,
  textarea: 58,
  // 6098:3113 / 6692:1501 / 6103:4062.
  chevron: 14,
  linkIcon: 13,
  iconButtonGlyph: 18,
  // 6034:4827.
  fieldLabelColumn: 105,
  // 6098:2606 / 2607.
  bannerTick: 26,
  bannerTickGlyph: 15
};

const PANEL_RADIUS: Partial<IResponseScale['radius']> = {
  // 6098:2253 / 3110 / 2600 / 2589; 6034:4849.
  panel: 12,
  row: 10,
  button: 8,
  field: 8,
  box: 7,
  pill: 999,
  // 6098:2244's 12-12-4-12 user bubble, mirrored for Orbit's.
  questionBubble: '4px 12px 12px 12px'
};

const PANEL_SCALE: IResponseScale = {
  type: {
    ...(Object.fromEntries(
      Object.entries(rsType).map(([step, value]) => [step, deriveType(value)])
    ) as IResponseScale['type']),
    ...PANEL_TYPE
  },
  size: { ...derive(rsSize, PANEL_RATIO.size), ...PANEL_SIZE },
  radius: { ...derive(rsRadius, PANEL_RATIO.radius), ...PANEL_RADIUS },
  space: panelSpace,
  // 6098:2323 / 2262 / 2588 / 3098 / 3140 labels; 6034:5075 rows and 6034:4849 boxes on the panel
  // fill; 6098:2589 / 2596 fields draw no rule.
  color: {
    label: rsColor.secondary,
    answerRow: rsColor.panel,
    boxed: rsColor.panel,
    fieldRule: 'transparent'
  }
};

const RESPONSE_SCALES: Record<ResponseVariant, IResponseScale> = {
  page: PAGE_SCALE,
  panel: PANEL_SCALE
};

export const responseScaleOf = (variant: ResponseVariant | null | undefined): IResponseScale =>
  RESPONSE_SCALES[variant ?? 'page'] ?? PAGE_SCALE;

export interface IResponseVariantTheme {
  orbitResponseVariant?: ResponseVariant;
}

export const responseVariantTheme: Record<ResponseVariant, IResponseVariantTheme> = {
  page: { orbitResponseVariant: 'page' },
  panel: { orbitResponseVariant: 'panel' }
};

interface IScaleProps {
  theme?: IResponseVariantTheme;
}

/** The scale a styled component draws at, from the nearest `ResponseScaleProvider`. */
export const rsFor = ({ theme }: IScaleProps): IResponseScale =>
  responseScaleOf(theme?.orbitResponseVariant);

/** Variant-aware reads for a styled template: `${rsv.size('panelHead')}px`. */
export const rsv = {
  size:
    <K extends keyof IResponseScale['size']>(key: K) =>
    (props: IScaleProps) =>
      rsFor(props).size[key],
  radius:
    <K extends keyof IResponseScale['radius']>(key: K) =>
    (props: IScaleProps) =>
      rsFor(props).radius[key],
  space:
    <K extends keyof IResponseScale['space']>(key: K) =>
    (props: IScaleProps) =>
      rsFor(props).space[key],
  color:
    <K extends keyof IResponseRoleColor>(key: K) =>
    (props: IScaleProps) =>
      rsFor(props).color[key],
  /** An actions row's top margin that lands it `key` under what precedes it (rows pull up a gap). */
  actionsTop:
    <K extends keyof IResponseScale['space']>(key: K) =>
    (props: IScaleProps) =>
      Number(rsFor(props).space[key]) - rsFor(props).space.buttonGap
};
